import { ref, readonly } from 'vue'

/**
 * État global de maintenance.
 *
 * Activé automatiquement quand une requête API renvoie un 503 avec
 * `{ maintenance: true }` (voir `src/services/api/client.js`). Quand il est
 * actif, `App.vue` affiche une page de maintenance plein écran qui coupe
 * l'accès à toute l'application.
 */
export const DEFAULT_MAINTENANCE_MESSAGE =
  "L'application est en cours de mise à jour. Merci de réessayer dans quelques instants."

const isMaintenance = ref(false)
const maintenanceMessage = ref(DEFAULT_MAINTENANCE_MESSAGE)

/**
 * Met à jour l'état de maintenance.
 * @param {boolean} active
 * @param {string} [message]
 */
export function setMaintenance(active, message) {
  isMaintenance.value = active
  if (message) {
    maintenanceMessage.value = message
  }
}

/** Adresse sondée : le middleware de maintenance répond sur tout `/api`. */
export const MAINTENANCE_PROBE_URL = '/api/health'

/**
 * « Réessayer » : la maintenance est-elle finie ?
 *
 * L'ancien bouton rechargeait la page à l'aveugle — et pendant un déploiement,
 * un rechargement tombe souvent sur une page blanche du navigateur. On demande
 * d'abord au serveur : toujours en maintenance, on reste sur l'écran (avec le
 * message à jour) ; sinon on recharge. Une panne réseau (serveur qui
 * redémarre) laisse aussi l'écran en place : il n'y a rien de mieux à montrer.
 *
 * @param {object} [deps] injectables pour les tests
 * @returns {Promise<boolean>} `true` si la page est rechargée
 */
export async function checkMaintenanceOver({
  fetchImpl = (...args) => fetch(...args),
  reload = () => window.location.reload()
} = {}) {
  try {
    const response = await fetchImpl(MAINTENANCE_PROBE_URL, { cache: 'no-store' })
    let data = null
    try {
      data = await response.json()
    } catch {
      data = null
    }
    if (response.status === 503 && data?.maintenance) {
      setMaintenance(true, data.message)
      return false
    }
    reload()
    return true
  } catch {
    return false
  }
}

export function useMaintenance() {
  return {
    isMaintenance: readonly(isMaintenance),
    maintenanceMessage: readonly(maintenanceMessage),
    setMaintenance,
    checkMaintenanceOver
  }
}
